import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteReminderOption, TireSeason, type TireQuoteRecordCustomer } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiSelectionCardGroup, NoddiSheet, useIsMobile } from 'noddi-ui-web';
import { CustomDateFormats, noddiFormatDate } from 'noddi-util';
import { commonTrackingEvents, tracking } from 'noddi-web';
import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../../Error/ApiErrorMessageWithTrans';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer: TireQuoteRecordCustomer;
};

/** Reminder by SMS on a date the backend offers; a new pick replaces the earlier one. */
export const TireOfferRemindSheet = ({ open, onOpenChange, offer }: Props) => {
  const isMobile = useIsMobile();
  const options = offer.reminderOptions ?? [];
  const [option, setOption] = useState<TireQuoteReminderOption | undefined>(options[0]?.option);
  const [remindOn, setRemindOn] = useState<string | null>(null);
  const { mutate, isPending, error, reset } = noddiAsync.usePost({
    type: URLKeys.postRemindTireOfferQuote,
    queryConfig: {
      onSuccess: () => {
        void invalidateQueryExactMatch({
          urlKey: URLKeys.getCarTireQuoteBySlug,
          input: { carTireQuoteSlug: offer.slug }
        });
      }
    }
  });
  const remindOnLabel = remindOn ? formatRemindOn(remindOn) : '';

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setRemindOn(null);
      reset();
    }
    onOpenChange(next);
  };

  const confirm = () => {
    const picked = options.find((item) => item.option === option);
    if (!picked) {
      return;
    }
    mutate(
      { carTireQuoteSlug: offer.slug, option: picked.option },
      {
        onSuccess: () => {
          tracking.track(commonTrackingEvents.tireOfferReminderSet, { slug: offer.slug, option: picked.option });
          setRemindOn(picked.remindOn);
        }
      }
    );
  };

  return (
    <NoddiSheet
      open={open}
      onOpenChange={handleOpenChange}
      side={isMobile ? 'bottom' : 'right'}
      title={t`When should we remind you?`}
      description={t`We send you a text message with the offer. Your tires keep wearing in the meantime.`}
      isSuccess={remindOn !== null}
      successTitle={t`Reminder set`}
      successDescription={t`We send you a text message on ${remindOnLabel}. Prices and availability can change before then.`}
      footer={
        remindOn === null && (
          <NoddiButton className='w-full' onClick={confirm} loading={isPending} disabled={!option}>
            <Trans>Remind me</Trans>
          </NoddiButton>
        )
      }
    >
      <div className='flex flex-col gap-4'>
        <NoddiSelectionCardGroup<TireQuoteReminderOption>
          size='small'
          value={option}
          onValueChange={setOption}
          options={options.map((item) => ({
            value: item.option,
            label: getReminderOptionLabel(item.option, offer.season.value),
            description: formatRemindOn(item.remindOn),
            icon: 'Bell'
          }))}
        />
        {error && <ApiErrorMessageWithTrans error={error} />}
      </div>
    </NoddiSheet>
  );
};

const getReminderOptionLabel = (option: TireQuoteReminderOption, season: TireSeason) => {
  switch (option) {
    case TireQuoteReminderOption.IN_ONE_WEEK:
      return t`In 1 week`;
    case TireQuoteReminderOption.IN_TWO_WEEKS:
      return t`In 2 weeks`;
    case TireQuoteReminderOption.IN_ONE_MONTH:
      return t`In 1 month`;
    case TireQuoteReminderOption.BEFORE_SEASON:
      return season === TireSeason.WINTER ? t`Before the autumn tire change` : t`Before the spring tire change`;
  }
};

const formatRemindOn = (date: string) => noddiFormatDate(date, CustomDateFormats.SHORT_WEEKDAY_DAY_MONTH_COMMA);
